// To update the controller SVG:
// 1) Convert the HTML formatted SVG file to a React friendly Javascript format use:
// 2) From the command line: npx @svgr/cli my_new_controller.svg > MyController.jsx
// 3) Cut the everything inside the <svg></svg> (remove any <defs> sections)
//    from the newly formated jsx file and paste(replace) in Controller.tsx.
//    Leave everything outside the <svg></svg> tags already in Controller.tsx
// 4) Do a search for <filename__> in className="<filename__>controller-button".
//    Replace <filename__> with empty space for all instances. The conversion
//    process adds the file name to many tags.
import React from 'react';
import './Controller.css';
import ButtonMappingDialog from './ButtonMappingDialog';
import { editor as MonacoEditor } from 'monaco-editor';
import { useColorScheme } from '@mui/material/styles';
import { useMappingUi } from './useMappingUi';

interface ControllerProps {
  editor?: MonacoEditor.IStandaloneCodeEditor;
}

const Controller: React.FC<ControllerProps> = ({ editor }) => {
  const { mode, systemMode } = useColorScheme();
  const visualMode = (mode === 'system' ? systemMode || 'light' : mode) as 'light' | 'dark';
  const { svgRef, dialogOpen, dialogButton, setDialogOpen, handleSvgMouseMove, handleSvgMouseLeave, handleSvgClick, handleSaveButtonBindings } =
    useMappingUi('controller', editor);

  return (
    <div className={`controller-container ${visualMode === 'dark' ? 'dark' : 'light'}`}>
      <svg
        viewBox="0 0 114.853 95.245"
        id="svg1"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        preserveAspectRatio="xMidYMid meet"
        xmlSpace="preserve"
        ref={svgRef}
        onMouseMove={handleSvgMouseMove}
        onMouseLeave={handleSvgMouseLeave}
        onClick={handleSvgClick}
      >
        <g id="layer1" transform="translate(-64.537 -56.567)">
          <path
            id="path9"
            d="m64.862 134.963-.007.098m.058-.801-.006.084M73.853 88.8v-.093m95.843.093v-.093m-47.922-10.79v.289m-44.319 3.291.228-.27.296-.227m24.448-3.57 1.1.107.942.132.715.14m-2.757-.378v.434m63.67 3.632-.228-.27-.297-.227m-24.45-3.57-1.1.107-.942.132-.717.143m2.76-.381v.436m24.746 3.36-.296-.227m-24.45-3.57-1.1.107-.943.132-.717.143m2.76-.381v.436M153.32 144.6q.041.082.084.162m-88.49-10.533-.04.569m84.72 1.124-.055-.14-.035-.09m-84.031-9.049-.064.828m-.495 6.758v.031m113.123-8.07.034.453m-83.91 8.747.963-2.564.421-1.196.36-1.071m-4.088 10.612.42-1.02.906-2.208m0 0 .51-1.27.506-1.283m57.568 5.781-.42-1.02-.906-2.208m-1.016-2.553-.963-2.564-.42-1.196-.36-1.071m4.117 10.686-.03-.074m-1.328-3.228-.51-1.27-.429-1.087-.077-.196m-53.215-5.638.01-.03m-.275.837.265-.807m51.472.807-.265-.807m0 0-.01-.03m-82.537 6.01.026-.667v0m3.471 9.18-1.418-1.89-1.119-2.075-.72-2.225-.24-2.32m7.064 11.845-1.87-1.572-1.7-1.762m9.92 6.728-2.26-.88-2.114-1.154-1.976-1.36m19.894-6.334-.214.513-.228.524-.23.51-.233.498-.233.484q-.254.497-.51.992l-1.065 1.794-1.513 1.969-1.014.981c-.733.642-1.579 1.047-2.444 1.469-.864.318-1.74.409-2.646.49l-1.254-.072-2.006-.434-1.971-.754-1.651-.847-1.574-.981-2.98-2.369-1.807-1.889-1.555-2.153-.932-1.818-.637-1.974-.236-1.534-.005-1.244m86.906 5.845.214.513.227.524.23.51.233.498.233.484.51.992m0-.005.95 1.617 1.107 1.54 1.323 1.402 1.166.9 1.323.69m3.262.554.355-.021m0-.003c1.658-.158 3.138-.75 4.67-1.347l2.114-1.154 1.976-1.36m0 0 1.87-1.571 1.7-1.762m0 0 1.418-1.892 1.119-2.074.72-2.226.24-2.32m0-.003-.026-.667v0m-31.67-6.345-.122-.302s-.071-.156-.12-.227c-.049-.073-.095-.158-.171-.201-.095-.054-.215-.046-.323-.056h-.21l-.282.019-.336.037-.916.143-.952.158-2.056.249-2.048.156-2.042.1m9.683.215-.106-.291m.233.661-.127-.367m.241.709-.114-.342m-50.729-.661.122-.302.106-.203m.106-.148.08-.077m0 0c.097-.052.214-.046.322-.055h.209l.283.018.336.037c.624.093 1.246.198 1.868.302l2.056.248 2.048.156 2.045.101m12.108.14h3.559m-15.666-.14 3.418.085m12.25-24.504h-6.323l-6.31-.01m-.004.003s-.379-.04-.553-.111a1.5 1.5 0 0 1-.468-.313 1.5 1.5 0 0 1-.312-.468c-.072-.174-.111-.553-.111-.553m0 0-.005-9.964v-9.97m0 0s.038-.384.11-.56a1.48 1.48 0 0 1 .792-.792c.175-.072.558-.11.558-.11m0 0 6.308-.011 6.316-.006m.003-3.265h-1.241l-4.964-.06-4.958-.085-4.943-.042-4.958.082-4.921.304-2.712.307-2.683.436-2.68.604-2.617.799-3.614 1.574-1.744 1.024-1.643 1.18-1.778 1.585-1.609 1.807m-.944 1.108.05-.034.1-.106.223-.26.423-.523.148-.185m-.973 1.111h.005m-5.511 14.798-.312 1.572-.511 2.95-.431 2.95-.392 3.165-.288 2.735m4.881-23.405-.51 1.283-.34.937-.19.561-.296.915-.283.94-.246.873-.212.788-.063.262m-2.744 16.846-.145 1.524-.408 4.736-.233 2.89m0-.003-.034.45m0 .003-.056.703-.161 2.13-.074.998-.069.96-.132 1.844-.07.985m-.004.082-.045.619m70.991-32.039s-.039.38-.11.553c-.073.173-.18.336-.313.468-.133.133-.295.24-.468.313-.174.072-.553.11-.553.11m0 0-6.31.009h-6.324m56.298 22.264c.069.867.162 2.095.29 3.831l.07.96.132 1.845.074 1.066.05.717m-1.436-18.018c.217 2.215.456 5.069.785 9.15m-3.212-24.734c.4 1.676.718 3.367 1.037 5.059.211 1.207.395 2.418.575 3.63q.134.957.259 1.916l.127 1.024.15 1.29.122 1.107.093.889.069.67m-7.446-28.171h-.005m-.023-.003h.024m-.024 0-.05-.034-.104-.106-.225-.26-.423-.523-.148-.185m-47.059-9.515h1.241l4.964-.06 4.958-.085 4.945-.043 4.958.082 4.921.305 2.712.307 2.683.436 2.68.603 2.617.8 3.614 1.574 1.744 1.024 1.646 1.18 1.778 1.585 1.608 1.807m-47.069-6.25 6.316.005 6.307.011m0 0s.385.04.561.114a1.49 1.49 0 0 1 .791.79c.073.176.111.56.111.56m0 0v9.969l-.008 9.964m17.354 41.452.035.074 1.233 2.11 1.513 1.968 1.014.981 1.17.847 1.274.622 1.421.38 1.225.11 1.254-.072 2.006-.434 1.971-.754 1.648-.847 1.575-.981 2.979-2.368 1.807-1.89 1.556-2.153.931-1.818.638-1.974.235-1.534.005-1.244m-.532-7.548.328 4.447m-8.749-43.519.047.064m.003 0 .498.749 1.077 1.878.928 1.98m-2.62-4.753-.028-.034-.18-.172m.275.288-.008-.01-.042-.053m-95.89.063-.048.064m.047-.064.008-.01.043-.053m-.098.127-.495.749-1.077 1.878-.929 1.98m100.285-1.604.247.487.505 1.119m0 0 .077.18m0 0 .36.891 1.518 4.657 1.13 4.77.852 4.792c.486 3.175.811 6.369 1.145 9.562l.41 4.739M149.588 135.9l-.053-.13-.36-.944m2.13 5.313-.9-2.196m-4.056-10.007c-.171-.005-.342.005-.513.013l-.344.037-.717.109-1.717.272-1.733.19-3.464.22-3.468.104-6.303.053h-6.315m-24.768-.977c.067.001.133-.013.199-.02.171-.006.342.004.513.012.356.032.708.093 1.061.146 1.143.215 2.296.336 3.45.463l3.464.22 3.468.103 6.303.053h6.313m-25.112-.55.172-.294m-.661 1.648.06-.185m-2.275 6.347c.144-.356.276-.716.413-1.074m-2.127 5.313.9-2.196M71.18 93.633l-.36.891-1.519 4.657-1.13 4.77-.852 4.792-.645 4.784-.5 4.778-.41 4.739-.27 3.37m5.76-32.961-.077.18m2.752-5.03.087-.1.013-.014m-.177.217.013-.021.027-.035.037-.047m-.059.098h-.01l-.01.005m.994-1.14-.58.706-.24.28-.114.12-.04.029m94.83-1.135.58.706.24.28.114.12.04.029m0 0h.009l.008.005m0 0-.013-.021-.026-.035-.037-.047m-47.842-10.398h1.241l4.964-.061 6.633-.109 6.607.024 6.54.363 3.643.442 3.588.695 3.482 1.022 2.469 1.01 2.354 1.26 2.186 1.521 1.693 1.506 1.527 1.685m-46.927-9.358h-1.24l-4.964-.061-6.633-.109-6.607.024-6.54.363-3.644.442-3.588.695-3.481 1.022-2.469 1.01-2.355 1.26-2.185 1.521-1.696 1.506-1.527 1.685m3.133-6.564.037-.027.111-.074.087-.055m.003 0 .058-.037.175-.104.143-.079m.013-.008.222-.119.471-.228.32-.14v0m-1.026.498.013-.01m1.855-.922 2.188-.926 2.27-.703 2.4-.516 2.436-.334 3.461-.211 3.503.023 5.71.403m-22.81 2.698.839-.434M165.568 81l-.037-.027-.11-.074-.088-.055m-.003 0-.058-.037-.175-.104-.143-.079m.003.003-.013-.01m0 0-.222-.12-.471-.227-.32-.14m-.003 0-.84-.435m0 0-2.187-.926-2.268-.703-2.4-.516-2.436-.334-3.461-.211-3.503.023-5.71.403v0m24.445 3.569-.037-.027-.111-.074-.087-.055m-.003 0-.059-.037-.174-.104-.143-.079m.003.003-.013-.01m0 0-.223-.12-.47-.228-.32-.14m-.004 0-.838-.434m0 0-2.188-.926-2.268-.704-2.4-.515-2.437-.334-3.46-.211-3.503.023-5.71.402v0m-44.841 51.986.095-.288.064-.18m0 0 .103-.291c.048-.125.109-.244.167-.365M73.758 88.88l-.122.175-.833 1.346-.924 1.747-.97 2.209-.79 2.159-.616 1.976-.476 1.744-.362 1.479-.27 1.193-.19.897-.08.394m106.701-2.714c-.411-1.891-1.01-3.73-1.595-5.572l-1.13-2.882-.971-1.99-1.185-1.97-.146-.196m7.284 52.694.233-.383.265-.537.314-.733.336-.958.294-1.193M73.855 88.698v.1m95.838-.002v-.1m-47.919-10.78-3.627-.018-3.262-.037-2.9-.035-2.543-.02-2.193-.006-1.919.01-3.172.061-3.437.154-3.654.304-2.587.333-2.599.463-2.29.543-1.943.585-1.62.592-.86.36-.748.344-.714.355-1.045.574-1.392.878-1.675 1.27-1.235 1.125-1.294 1.4-1.109 1.457m95.642-.008-1.109-1.458-1.294-1.4-1.235-1.124-1.675-1.27-1.392-.878-1.045-.574-.714-.355-.749-.344-.86-.36-2.074-.74-2.588-.715-3.135-.624-3.241-.437-2.81-.246-2.688-.15-2.546-.083-2.383-.037-2.345-.008-2.738.014-3.228.034-3.73.045-4.24.024m-44.338 3.625-.061 2.833-.01.447m88.81 0-.011-.447-.061-2.833m.071 3.28-.01-.447"
            className="cls-3"
            style={{
              display: 'inline',
              fill: 'none',
              stroke: '#000',
              strokeWidth: 0.588,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeDasharray: 'none',
            }}
          />
          <g
            id="g1"
            style={{
              display: 'inline',
              fill: '#f0f',
              fillOpacity: 1,
              stroke: '#000',
              strokeOpacity: 1,
            }}
            transform="matrix(.08784 0 0 .08784 71.888 98.086)"
          >
            <path
              id="path15-5"
              d="M538.321 250.23c-3.66 0-6.64-3-6.64-6.7s2.98-6.71 6.64-6.71 6.65 3.01 6.65 6.71-2.98 6.7-6.65 6.7m0-17.89c-6.14 0-11.13 5.02-11.13 11.19v17.43c0 1.24 1.01 2.24 2.24 2.24s2.24-1 2.24-2.24v-8.46c1.91 1.44 4.23 2.22 6.64 2.22 6.13 0 11.13-5.02 11.13-11.19s-4.99-11.19-11.13-11.19"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 2.14034,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path16-0"
              d="M517.551 241.81c-.13-.02-.24-.04-.37-.06l-.41-.08c-2.59-.45-3.56-.98-3.67-1.99-.06-.5.08-.96.41-1.37.5-.62 1.42-1.04 2.52-1.16.13-.02.25-.03.37-.04 1.26-.04 2.52.41 3.85 1.36.57.4 1.25.81 1.99.79.35-.01.69-.12.98-.33.99-.65 1.27-1.96.64-3.04-1.36-2.34-4.74-3.3-7.49-3.22-.28.02-.55.03-.81.06-2.09.23-3.94 1.09-5.21 2.45-1.29 1.38-1.9 3.14-1.7 4.97.51 4.66 5.46 5.55 7.33 5.87l.79.14c2.6.46 3.55.98 3.67 1.99.14 1.29-1.07 2.33-2.93 2.53-.15.02-.3.03-.44.03-1.75.05-3.42-.73-4.96-2.32-.48-.5-1.23-1.17-2.14-1.13-.25.01-.49.07-.73.18-1.11.5-1.61 1.79-1.12 2.93 1.01 2.43 4.52 4.93 9.01 4.79.28 0 .57-.03.86-.06 4.34-.47 7.32-3.66 6.91-7.42-.5-4.67-5.45-5.54-7.33-5.87"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 2.14034,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path17-0"
              d="M624.391 250.23c-3.89 0-7.06-3-7.06-6.7s3.17-6.71 7.06-6.71 7.07 3.01 7.07 6.71-3.18 6.7-7.07 6.7m0-17.89c-6.37 0-11.55 5.02-11.55 11.19s5.18 11.19 11.55 11.19 11.56-5.02 11.56-11.19-5.19-11.19-11.56-11.19"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 2.14034,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path18-8"
              d="M579.731 243.62v-.09c0-3.7 3.17-6.7 7.07-6.7 2.14 0 4.15.91 5.48 2.48zm7.07-11.28c-6.37 0-11.56 5.02-11.56 11.19s5.18 11.19 11.56 11.19c3.56 0 6.88-1.55 9.09-4.27.78-.96.63-2.37-.33-3.15-.4-.32-.91-.5-1.42-.5-.68 0-1.31.31-1.73.83-1.35 1.66-3.4 2.61-5.61 2.61s-4.11-.9-5.45-2.44l14.95-5.13c.57-.2 1.03-.62 1.28-1.16.28-.54.3-1.18.09-1.73-1.64-4.44-6.01-7.43-10.88-7.43"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 2.14034,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path19"
              d="M610.581 233.02c-6.9 0-10.7 3.97-10.7 11.19v7.87c0 1.23 1 2.24 2.25 2.24s2.24-1 2.24-2.24v-7.87c0-4.64 1.91-6.7 6.22-6.7 1.24 0 2.25-1.01 2.25-2.24s-1-2.24-2.25-2.24"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 2.14034,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path20-3"
              d="M562.461 232.34c-2.15 0-4.25.65-6.01 1.84v-7.82c0-1.24-1-2.25-2.24-2.25s-2.24 1.01-2.24 1.99v25.71c0 1.48 1.01 2.5 2.24 2.5a2.25 2.25 0 0 0 2.24-2.24v-9.56c0-3.13 2.69-5.68 6.01-5.68s6 2.55 6 5.68v9.57c0 1.23 1.01 2.24 2.25 2.24s2.24-1 2.24-2.24v-9.57c0-5.61-4.7-10.17-10.49-10.17"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 2.14034,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path21"
              d="M567.87 191.487c-18.144 0-32.867-14.723-32.867-32.868 0-18.144 14.723-32.868 32.868-32.868 18.144 0 32.89 14.724 32.89 32.868s-14.724 32.868-32.89 32.868zm0-75.113h-42.744c-1.404 0-1.834 1.177-.566 1.88 1.767.996 9.469 5.39 9.469 5.39 5.278 3.195 1.381 7.974 1.381 7.974a41.97 41.97 0 0 0-9.785 27.001c0 23.332 18.914 42.246 42.246 42.246 23.331 0 42.245-18.914 42.245-42.246 0-23.331-18.914-42.245-42.245-42.245z"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#4794ff',
                fillOpacity: 0,
                stroke: '#000',
                strokeWidth: 6.8195,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path22"
              d="M593.92 158.62c0 5.3-2.944 9.581-6.569 9.581s-6.569-4.304-6.569-9.582 2.945-9.581 6.57-9.581c3.624 0 6.568 4.303 6.568 9.581z"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#1900ff',
                fillOpacity: 0,
                stroke: '#000',
                strokeWidth: 6.8195,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path23-7"
              d="M574.417 158.62c0 7.43-4.123 13.455-9.22 13.455-5.096 0-9.219-6.026-9.219-13.456s4.123-13.455 9.22-13.455c5.096 0 9.219 6.026 9.219 13.455z"
              className="cls-1"
              style={{
                display: 'inline',
                fill: '#1900ff',
                fillOpacity: 0,
                stroke: '#000',
                strokeWidth: 6.8195,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <g id="up_button" className="controller-button" data-button="up" transform="translate(21.167 29.633)">
            <circle
              id="path1-2-3-3-4"
              cx={65.846}
              cy={62.571}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.611,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path60"
              d="m67.246 63.605-1.124-2.791a.3.3 0 0 0-.132-.156l-.016-.008q0 0 0 0a.3.3 0 0 0-.111-.027.26.26 0 0 0-.17.061.3.3 0 0 0-.092.13l-1.128 2.791a.255.255 0 0 0 .315.333c.23-.105.625-.177 1.072-.177s.841.072 1.071.177a.256.256 0 0 0 .315-.333"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.532,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
              }}
            />
          </g>
          <g id="a_button" className="controller-button" data-button="a" transform="translate(21.167 29.633)">
            <circle
              id="path1-2-3-4"
              cx={135.322}
              cy={78.305}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.611,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path59"
              d="m136.734 79.517-1.125-2.789a.3.3 0 0 0-.132-.156l-.016-.008a.3.3 0 0 0-.111-.026.27.27 0 0 0-.17.06.3.3 0 0 0-.092.13l-1.124 2.789a.258.258 0 0 0 .325.33.3.3 0 0 0 .156-.153l.23-.56q.024.004.048.005h1.299l.23.555c.027.064.09.13.156.154a.258.258 0 0 0 .326-.331zm-1.852-.889.463-1.132.468 1.132z"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.332,
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
              }}
            />
          </g>
          <g id="down_button" className="controller-button" data-button="down" transform="translate(21.167 29.633)">
            <circle
              id="path1-2-3-3-4-1-3"
              cx={65.799}
              cy={78.329}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.611,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path61"
              d="m64.473 77.302 1.125 2.792c.032.077.08.127.132.156l.016.008v0a.3.3 0 0 0 .111.026.27.27 0 0 0 .17-.06.3.3 0 0 0 .092-.13l.992-2.458.135-.334a.255.255 0 0 0-.315-.333c-.23.106-.624.177-1.071.177s-.842-.071-1.072-.177a.256.256 0 0 0-.315.333"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.532,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                paintOrder: 'normal',
              }}
            />
          </g>
          <g id="x_button" className="controller-button" data-button="x" transform="translate(21.167 29.633)">
            <circle
              id="path1-2-3"
              cx={127.46}
              cy={70.483}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.611,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path56"
              d="m127.439 70.878-.968 1.149a.27.27 0 0 1-.217.11.254.254 0 0 1-.252-.253c0-.066.014-.117.056-.167l1.042-1.24-1.018-1.218a.27.27 0 0 1-.066-.177c0-.154.121-.27.27-.27a.27.27 0 0 1 .214.098l.958 1.143.968-1.148a.254.254 0 0 1 .452.161.22.22 0 0 1-.06.161l-.683.81-.347.413 1.037 1.235a.271.271 0 0 1-.204.45.26.26 0 0 1-.214-.103l-.973-1.159Z"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.332,
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
              }}
            />
          </g>
          <g id="right_button" className="controller-button" data-button="right" transform="translate(21.167 29.633)">
            <circle
              id="path1-2-3-3"
              cx={73.72}
              cy={70.443}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.611,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path62"
              d="m72.71 71.89 2.791-1.125a.3.3 0 0 0 .156-.133l.008-.016q0 0 0 0a.3.3 0 0 0 .027-.11.26.26 0 0 0-.061-.17.3.3 0 0 0-.13-.093l-2.791-1.127a.255.255 0 0 0-.333.315c.105.23.177.625.177 1.072s-.072.841-.177 1.071a.256.256 0 0 0 .333.315"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.532,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
              }}
            />
          </g>
          <g id="left_button" className="controller-button" data-button="left" transform="translate(21.167 29.633)">
            <circle
              id="path1-2-3-3-4-1"
              cx={57.834}
              cy={70.485}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.611,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path63"
              d="m58.962 69.116-2.791 1.125a.3.3 0 0 0-.156.132l-.008.016q0 0 0 0a.3.3 0 0 0-.027.111c0 .066.024.124.061.17a.3.3 0 0 0 .13.092l2.791 1.127a.255.255 0 0 0 .333-.315c-.105-.23-.177-.624-.177-1.071s.072-.842.178-1.072a.256.256 0 0 0-.334-.315"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.532,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
              }}
            />
          </g>
          <path
            id="path64"
            d="M160.467 111.577a.3.3 0 0 0-.15-.149.246.246 0 0 0-.313.318l.606 1.474c.029.074.074.121.127.148l.016.008q0 0 0 0a.24.24 0 0 0 .108.024q.097-.002.162-.059a.3.3 0 0 0 .087-.124l1.077-2.672a.247.247 0 0 0-.312-.318.28.28 0 0 0-.151.148l-.86 2.107-.397-.908z"
            className="cls-4"
            style={{
              fill: '#f0f',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.232,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
            }}
          />
          <g id="b_button" className="controller-button" data-button="b" transform="translate(21.167 29.633)">
            <circle
              id="path1-2"
              cx={143.22}
              cy={70.459}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.611,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path57"
              d="M143.936 70.447c.328-.124.463-.418.463-.696 0-.495-.4-.9-.918-.9h-.826a.273.273 0 0 0-.272.27v2.702c0 .148.124.272.272.272h.863a.91.91 0 0 0 .926-.907c0-.307-.159-.617-.51-.74zm-.537-1.087c.259 0 .439.19.439.426 0 .235-.19.426-.426.426h-.482v-.852zm.037 2.227h-.506v-.87h.519a.437.437 0 1 1-.013.873z"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.332,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
          </g>
          <path
            id="path65"
            d="M170.01 102.814c-.28 0-.537.068-.756.187a1 1 0 0 1-.072-.259.127.127 0 0 0-.145-.095.12.12 0 0 0-.074.045l-.908 1.175a.14.14 0 0 0-.034.095v.008c0 .018.008.037.016.056a.11.11 0 0 0 .069.055q.031.011.076.008l1.469-.217a.13.13 0 0 0 .076-.045.124.124 0 0 0-.013-.172 1 1 0 0 1-.209-.211c.146-.082.315-.13.503-.13.585 0 1.05.484 1.05 1.095s-.465 1.093-1.05 1.093c-.32 0-.585-.138-.773-.347-.071-.06-.143-.103-.209-.103a.26.26 0 0 0-.262.257c0 .058.022.13.072.177.27.31.688.519 1.172.519.886 0 1.587-.707 1.587-1.598s-.7-1.598-1.587-1.598z"
            className="cls-4"
            style={{
              fill: '#f0f',
              fillOpacity: 1,
              stroke: '#000',
              strokeWidth: 0.232,
              strokeLinejoin: 'round',
              strokeMiterlimit: 10,
              strokeDasharray: 'none',
            }}
          />
          <g id="y_button" className="controller-button" data-button="y" transform="translate(21.167 29.633)">
            <circle
              id="path1"
              cx={135.271}
              cy={62.549}
              r={3.761}
              style={{
                display: 'inline',
                fill: 'none',
                stroke: '#000',
                strokeWidth: 0.611,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path58"
              d="m135.078 62.774-1.104-1.315a.27.27 0 0 1-.066-.177c0-.154.122-.27.27-.27a.27.27 0 0 1 .214.098l.969 1.154.976-1.16a.253.253 0 0 1 .45.162.24.24 0 0 1-.056.161l-1.106 1.32v1.316a.276.276 0 0 1-.272.272.276.276 0 0 1-.273-.272v-1.291Z"
              className="cls-4"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.332,
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
              }}
            />
          </g>
          <g
            id="g2"
            style={{
              display: 'inline',
            }}
            transform="matrix(1.01619 0 0 1.01619 -18.301 28.236)"
          >
            <path
              id="path17-7"
              d="M118.87 77.941c-3.7.634-3.214.156-5.732 2.585-1.571 1.515-1.678 2.016-2.227 4.29-.466 1.933.192 2.951.192 4.107 0 1.709 2.29 3.906 3.734 4.639 2.637 1.34.693 1.065 3.626 1.376 2.924 0 5.485 0 7.8-3.479.82-1.231 1.436-2.926 1.563-4.515.096-1.21-.285-2.005-.419-3.308-.552-1.639-1.247-2.717-2.758-3.932-2.694-2.166-2.759-1.578-5.78-1.763m.55 3.357c3.16 0 5.032 1.894 5.031 5.053 0 .552-.146 1.41-.362 1.89-1.01 2.247-2.014 2.818-4.668 3.071-3.16 0-4.777-1.893-5.076-4.984.207-3.182 1.87-4.869 5.076-5.03"
              style={{
                display: 'inline',
                fill: '#c7c7c7',
                fillOpacity: 0,
                stroke: '#000',
                strokeWidth: 0.201735,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="left_thumbstick_back"
              d="M125.904 86.334a6.53 6.53 0 0 0-6.529-6.529 6.53 6.53 0 0 0-6.528 6.529 6.53 6.53 0 0 0 6.528 6.529 6.53 6.53 0 0 0 6.529-6.529"
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.578634,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
              }}
            />
            <path
              id="left_thumbstick_back-5"
              d="M127.872 86.418a8.51 8.51 0 0 0-2.593-6.13 8.54 8.54 0 1 0 2.593 6.13"
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.578634,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
              }}
            />
            <g
              id="g9"
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: 'red',
                strokeOpacity: 1,
              }}
              transform="translate(1.9 1.376)scale(.98407)"
            >
              <circle
                id="circle12"
                cx={119.376}
                cy={86.313}
                r={5.138}
                style={{
                  display: 'inline',
                  fill: 'none',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.588,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  strokeDasharray: 'none',
                  strokeDashoffset: 0,
                  strokeOpacity: 1,
                }}
                transform="translate(.001)"
              />
            </g>
          </g>
          <g
            id="g3"
            style={{
              display: 'inline',
              fill: '#e80092',
              fillOpacity: 1,
            }}
            transform="translate(21.167 29.633)"
          >
            <path
              id="path17"
              d="M123.034 78.345c-2.369-.76-3.862-1.071-6.305-.277-2.903.943-3.931 1.95-4.643 3.534-.491 1.093-1.124 2.438-1.124 3.59 0 .865-.741 2.253.31 4.007 1.525 2.548 1.643 3.296 2.821 3.998 3.276 1.95 4.515 2.303 6.65 1.789 1.94.397 4.3-1.863 4.759-2.369 1.193-1.314 1.704-1.524 2.498-4.857 0-1.87.19-2.854-.546-4.758-.931-2.408-1.907-3.665-4.42-4.657m-3.62 2.893a5.088 5.088 0 1 1 0 10.176 5.088 5.088 0 0 1 0-10.176"
              style={{
                display: 'inline',
                fill: '#ff0039',
                fillOpacity: 0,
                stroke: '#000',
                strokeWidth: 0.167,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
            />
            <path
              id="path17-1"
              d="M140.544 109.337a6.634 6.634 0 1 0 0 13.268 6.634 6.634 0 0 0 0-13.268"
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.588,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeDashoffset: 0,
                strokeOpacity: 1,
              }}
              transform="translate(-21.167 -29.633)"
            />
            <path
              id="left_thumbstick_back-5-1"
              d="M128.142 86.383a8.677 8.677 0 0 0-8.677-8.678 8.677 8.677 0 0 0-8.678 8.678 8.677 8.677 0 0 0 8.678 8.677 8.677 8.677 0 0 0 8.677-8.677"
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.588,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
              }}
            />
            <g
              id="g10"
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
              }}
              transform="translate(-.012)"
            >
              <circle
                id="path1-2-3-4-3"
                cx={119.376}
                cy={86.313}
                r={5.138}
                style={{
                  display: 'inline',
                  fill: 'none',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.588,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  strokeDasharray: 'none',
                  strokeDashoffset: 0,
                  strokeOpacity: 1,
                }}
                transform="translate(.012)"
              />
            </g>
          </g>
          <g
            id="g4"
            style={{
              strokeWidth: 1.70539,
            }}
            transform="matrix(.59072 0 0 .58207 62.533 50.866)"
          >
            <path
              id="text17"
              d="M150.95 123.689q-.098.137-.094.367h-.543q.009-.232.075-.44.072-.183.225-.338.114-.11.271-.167t.386-.058q.424 0 .684.232.26.23.26.619 0 .275-.154.464-.098.118-.204.161.08 0 .228.144.223.217.223.593 0 .396-.262.696-.259.3-.769.3-.629 0-.873-.433-.129-.23-.143-.601h.571q0 .186.057.309.106.226.386.226.171 0 .298-.123.128-.125.128-.357 0-.307-.236-.41-.135-.058-.425-.058v-.438q.284-.004.396-.058.194-.09.194-.366 0-.178-.1-.29-.098-.112-.278-.112-.206 0-.302.138"
              className="cls-4"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.38759,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path55-9"
              d="m149.838 125.815-.84-1.006h.06c.316-.08.587-.38.587-.815 0-.524-.404-.952-.995-.952h-.767a.267.267 0 0 0-.267.264v2.682c0 .144.123.268.267.268a.27.27 0 0 0 .267-.268v-1.045h.263l1.021 1.212a.264.264 0 0 0 .47-.164.25.25 0 0 0-.062-.176zm-1.144-1.373h-.548v-.906h.484c.27 0 .46.204.46.458 0 .23-.16.421-.4.448z"
              className="cls-4"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.38759,
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
              }}
            />
          </g>
          <g
            id="g5"
            style={{
              strokeWidth: 1.71887,
            }}
            transform="matrix(.58507 0 0 .5785 40.255 50.98)"
          >
            <path
              id="text17-9"
              d="M106.565 94.71q-.11.154-.105.412h-.609q.01-.26.085-.494.08-.204.251-.378.128-.122.305-.187.176-.065.432-.065.475 0 .766.26.292.257.292.693 0 .308-.173.52-.11.133-.23.18.09 0 .257.162.25.243.25.665 0 .443-.293.78-.29.335-.862.335-.705 0-.98-.484-.143-.258-.16-.675h.641q0 .21.064.347.119.253.432.253.192 0 .334-.137.144-.14.144-.4 0-.344-.265-.46-.151-.065-.476-.065v-.491q.318-.005.444-.065.217-.101.217-.41 0-.2-.112-.325-.11-.125-.311-.125-.231 0-.338.154z"
              className="cls-4"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.437709,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
              transform="translate(-16.37 29.634)"
            />
            <path
              id="path53-5"
              d="M103.396 94.293a.3.3 0 0 1 .3-.299.3.3 0 0 1 .298.3v2.74h1.07c.153 0 .273.123.273.28s-.124.28-.273.28h-1.369c-.16 0-.299-.138-.299-.299v-2.998z"
              className="cls-4"
              style={{
                display: 'inline',
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.437709,
                strokeLinejoin: 'round',
                strokeMiterlimit: 10,
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
              transform="translate(-16.37 29.634)"
            />
          </g>
          <g id="r4_button" className="controller-button" data-button="r4" transform="rotate(90 68.16 151.446)">
            <g
              id="g11"
              style={{
                strokeWidth: 1,
                strokeDasharray: 'none',
              }}
            >
              <rect
                id="rect13"
                width={54.87}
                height={39.44}
                x={224.85}
                y={582.99}
                className="cls-3"
                rx={14.24}
                ry={14.24}
                style={{
                  fill: 'none',
                  stroke: '#000',
                  strokeWidth: 2.60157,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  strokeDasharray: 'none',
                }}
                transform="matrix(.26458 0 0 .26433 -10.594 -74.007)"
              />
            </g>
            <g
              id="g55"
              style={{
                strokeWidth: 1.05878,
                strokeDasharray: 'none',
              }}
              transform="matrix(.32617 0 0 .32586 -26.142 -111.07)"
            >
              <path
                id="path54"
                d="M254.4 601.82c0 .42-.34.72-.71.72-.18 0-.34-.05-.47-.18l-5.37-4.49a.77.77 0 0 1-.27-.6c0-.43.34-.77.76-.77h4.62v-.69c0-.41.33-.72.73-.72s.71.31.71.72v.69h2.13c.43 0 .79.34.79.77s-.35.77-.79.77h-2.13v3.77zm-4.3-3.77 2.86 2.43v-2.43z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.680644,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
              <path
                id="path55"
                d="m255.94 602.99-3.01 2.52v-.18c-.24-.95-1.14-1.76-2.44-1.76-1.57 0-2.85 1.21-2.85 2.98v2.3c0 .43.35.8.79.8h8.03c.43 0 .8-.37.8-.8s-.37-.8-.8-.8h-3.13v-.79l3.63-3.06a.792.792 0 0 0-.49-1.41c-.19 0-.35.04-.53.19zm-4.11 3.43v1.64h-2.71v-1.45c0-.81.61-1.38 1.37-1.38.69 0 1.26.48 1.34 1.2z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.680644,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
            </g>
          </g>
          <g id="l4_button" className="controller-button" data-button="l4" transform="rotate(-90 154.32 130.387)">
            <g id="g7" transform="matrix(.26458 0 0 .26433 -10.594 -74.007)">
              <rect
                id="rect32"
                width={54.87}
                height={39.44}
                x={561.59}
                y={582.99}
                className="cls-3"
                rx={14.24}
                ry={14.24}
                style={{
                  fill: 'none',
                  stroke: '#000',
                  strokeWidth: 2.60157,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                  strokeDasharray: 'none',
                }}
                transform="rotate(180 589.025 602.71)"
              />
            </g>
            <g
              id="g53"
              style={{
                strokeWidth: 1.05878,
                strokeDasharray: 'none',
              }}
              transform="matrix(.33821 0 0 .33788 -53.945 -118.378)"
            >
              <path
                id="path52"
                d="M586.83 603.45c0-.42.34-.72.71-.72.18 0 .34.05.47.18l5.37 4.49c.16.14.27.34.27.6 0 .43-.34.77-.76.77h-4.62v.69c0 .41-.33.72-.73.72s-.71-.31-.71-.72v-.69h-2.13c-.43 0-.79-.34-.79-.77s.35-.77.79-.77h2.13v-3.77zm4.3 3.77-2.86-2.43v2.43z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.680644,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
              <path
                id="path53"
                d="M592.79 596.25c.45 0 .8.37.8.8s-.35.8-.8.8h-7.32v2.86c0 .41-.33.73-.75.73s-.75-.33-.75-.73v-3.66c0-.43.37-.8.8-.8h8.01z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.680644,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
            </g>
          </g>
          <g id="g13" transform="translate(15.846 89.074)">
            <path
              id="path9-1"
              d="m129.825 44.995-4.37 4.887"
              style={{
                fill: '#14a1e9',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 1.02591,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path10-4"
              d="M410.25 147.25c-.11.039-2.876-2.462-2.965-2.538-.09-.076-2.987-2.423-2.966-2.538.022-.115 3.57-1.26 3.681-1.299.11-.039 3.592-1.375 3.68-1.299.09.076-.693 3.722-.715 3.837-.021.115-.605 3.798-.715 3.837"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.941102,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
              transform="matrix(.26439 .01024 -.01024 .26439 23.826 2.355)"
            />
          </g>
          <g id="g13-9-6" transform="matrix(.93548 .10708 .26871 -.88805 -38.182 100.643)">
            <path
              id="path9-1-1-9"
              d="m129.825 44.995-4.37 4.887"
              style={{
                fill: '#000',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 1.02591,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path10-4-4-0"
              d="M410.25 147.25c-.11.039-2.876-2.462-2.965-2.538-.09-.076-2.987-2.423-2.966-2.538.022-.115 3.57-1.26 3.681-1.299.11-.039 3.592-1.375 3.68-1.299.09.076-.693 3.722-.715 3.837-.021.115-.605 3.798-.715 3.837"
              className="cls-4"
              style={{
                fill: '#ff01ff',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.941102,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
              transform="matrix(.26439 .01024 -.01024 .26439 23.826 2.355)"
            />
          </g>
          <g id="g13-9-6-2" transform="matrix(-.93548 .10708 -.26871 -.88805 281.44 100.64)">
            <path
              id="path9-1-1-9-1"
              d="m129.825 44.995-4.37 4.887"
              style={{
                fill: '#14a1e9',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 1.02591,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path10-4-4-0-9"
              d="M410.25 147.25c-.11.039-2.876-2.462-2.965-2.538-.09-.076-2.987-2.423-2.966-2.538.022-.115 3.57-1.26 3.681-1.299.11-.039 3.592-1.375 3.68-1.299.09.076-.693 3.722-.715 3.837-.021.115-.605 3.798-.715 3.837"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.941102,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
              transform="matrix(.26439 .01024 -.01024 .26439 23.826 2.355)"
            />
          </g>
          <g id="g14" transform="matrix(-1 0 0 1 227.653 89.074)">
            <path
              id="path13"
              d="m129.825 44.995-4.37 4.887"
              style={{
                fill: '#000',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 1.02591,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <path
              id="path14"
              d="M410.25 147.25c-.11.039-2.876-2.462-2.965-2.538-.09-.076-2.987-2.423-2.966-2.538.022-.115 3.57-1.26 3.681-1.299.11-.039 3.592-1.375 3.68-1.299.09.076-.693 3.722-.715 3.837-.021.115-.605 3.798-.715 3.837"
              className="cls-4"
              style={{
                fill: '#f0f',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.941102,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
              transform="matrix(.26439 .01024 -.01024 .26439 23.826 2.355)"
            />
          </g>
          <g id="r1_button" className="controller-button" data-button="r1" transform="translate(92.404 38.723)">
            <path
              id="path1-9"
              d="m81.572 23.35.868.244v0m-.003 0c.298.081.572.224.82.4.182.127.332.297.476.467m0 0c.442.597.461 1.236.206 1.903q-.624 1.158-1.251 2.313c-.383.521-1.065.401-1.64.402-1.448.01-6.005.018-13.245.008h-7.48c-.34-.02-.532-.221-.709-.495q-1.24-3.357-2.46-6.724s-.194-.471-.26-.716c-.065-.24-.15-.487-.132-.735.036-.488.169-1.006.472-1.39.31-.392.792-.652 1.27-.796.263-.079.823-.02.823-.02l2.86.49s6.601 1.358 9.882 2.123c.809.189 2.416.608 2.416.608a350 350 0 0 1 7.072 1.92"
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.687917,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <g id="g8" transform="matrix(.26458 0 0 .26433 -9.407 -73.618)">
              <path
                id="path7"
                d="M310.89 368.57h-1.4c-.51 0-.94-.41-.94-.92s.43-.94.94-.94h2.43c.55 0 .99.44.99.99v10.55c0 .57-.44 1.03-.99 1.03s-1.03-.46-1.03-1.03z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.877275,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
              <path
                id="path8"
                d="m306.47 377.56-3.3-3.94.23-.02c1.24-.3 2.31-1.47 2.31-3.17 0-2.06-1.58-3.72-3.9-3.72h-3.01c-.57 0-1.05.46-1.05 1.03v10.5c0 .57.48 1.05 1.05 1.05s1.05-.48 1.05-1.05v-4.09h1.04l4 4.75c.21.27.5.39.82.39.57 0 1.03-.46 1.03-1.03 0-.25-.05-.46-.25-.69zm-4.49-5.37h-2.14v-3.55h1.9c1.06 0 1.81.8 1.81 1.79 0 .9-.63 1.65-1.57 1.76z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.877275,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
            </g>
          </g>
          <g id="l1_button" className="controller-button" data-button="l1" transform="translate(-50.849 39.024)">
            <path
              id="path1-6"
              d="M117.489 25.39c.013.24.104.453.194.671q.625 1.159 1.254 2.316c.08.14.19.238.32.309.106.058.233.064.354.085l1.151.008c4.88.017 9.758.007 14.637.005h5.906a.9.9 0 0 0 .46-.161c.07-.046.17-.183.17-.183l1.037-2.765q.882-2.411 1.76-4.826c.144-.5.173-1.005.015-1.467a2.26 2.26 0 0 0-.92-1.166c-.445-.273-.97-.295-1.532-.31l-5.72 1.04c-2.354.47-4.687 1.023-7.023 1.572-1.603.393-3.198.814-4.794 1.235-1.779.478-3.552.973-5.326 1.47a3.6 3.6 0 0 0-1.064.467m0 0a2 2 0 0 0-.249.209c-.082.08-.153.17-.227.259a2.16 2.16 0 0 0-.402 1.232"
              style={{
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.688,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <g id="g6" transform="matrix(.26458 0 0 .26433 -11.497 -74.14)">
              <path
                id="path1-43"
                d="M527.87 367.75a1.05 1.05 0 1 1 2.1 0v9.57h3.74c.53 0 .96.43.96.98s-.43.98-.96.98h-4.79c-.57 0-1.05-.48-1.05-1.05z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.877275,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
              <path
                id="path2-9"
                d="M539.35 368.57h-1.4c-.51 0-.94-.41-.94-.92s.43-.94.94-.94h2.43c.55 0 .99.44.99.99v10.55c0 .57-.44 1.03-.99 1.03s-1.03-.46-1.03-1.03z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.877275,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
            </g>
          </g>
          <g
            id="l2_button"
            className="controller-button"
            data-button="l2"
            style={{
              display: 'inline',
            }}
            transform="translate(-52.092 37.863)"
          >
            <path
              id="path1-4"
              d="M120.592 33.236s-.13-.276-.146-.425c-.032-.286-.022-.593.09-.857a1.63 1.63 0 0 1 .6-.713c.24-.155.528-.207.82-.236h3.641c2.43.005 4.858.003 7.287.003h7.879c.501.016 1.01.151 1.315.51.227.268.252.867.215 1.033-.062.538-.283 1.032-.466 1.536l-.881 2.432a3.14 3.14 0 0 1-1.223 1.49c-.464.309-1.045.388-1.603.474 0 0-.72.103-1.082.143-1.439.16-2.88.309-4.326.39-1.227.07-2.457.096-3.686.088a46 46 0 0 1-2.587-.095c-.47-.03-.943-.037-1.405-.124-.349-.065-.716-.117-1.021-.296-.547-.32-.968-.784-1.355-1.332m0 0-.498-.968-.785-1.528-.786-1.527"
              style={{
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.688,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <g id="g12" transform="matrix(.26458 0 0 .26433 -10.083 -73.87)">
              <path
                id="path3"
                d="M525.99 406.87c0-.58.48-1.04 1.04-1.04s1.04.46 1.04 1.04v9.56h3.74c.53 0 .96.42.96.97s-.42.97-.96.97h-4.78c-.57 0-1.04-.48-1.04-1.04z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.877275,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
              <path
                id="path4"
                d="M535.23 417.42c0-.19.07-.42.27-.66l5.17-6.09c.35-.41.51-.8.51-1.22 0-.96-.76-1.72-1.72-1.72s-1.66.74-1.7 1.68c-.04.55-.48 1.01-1.04 1.01s-1.04-.48-1.04-1.04c0-2.05 1.68-3.58 3.79-3.58s3.79 1.52 3.79 3.58c0 .69-.28 1.43-.89 2.12l-4.27 4.99h4.21c.51 0 .92.41.92.92s-.41.96-.92.96h-6.13c-.53 0-.96-.42-.96-.96z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.877275,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
            </g>
          </g>
          <g
            id="r2_button"
            className="controller-button"
            data-button="r2"
            style={{
              display: 'inline',
            }}
            transform="translate(93.793 37.863)"
          >
            <path
              id="path1-0"
              d="m72.357 39.105-3.685-.087c-2.038-.18-4.076-.326-6.097-.643-1.042-.278-1.616-.943-2.138-1.855l-1.254-3.46a1.6 1.6 0 0 1-.01-1.018c.096-.302.302-.585.56-.768.252-.178.564-.22.887-.265 5.937.005 11.875-.003 17.812-.003h.995c.296-.003.593.064.82.235.354.267.623.691.706 1.126.056.289-.06.575-.158.867l-2.07 4.023c-.316.608-.796 1.032-1.354 1.332-.517.278-1.13.307-1.722.365-.92.083-1.841.11-2.763.14-.177.006-.532.008-.532.008"
              style={{
                display: 'inline',
                fill: 'none',
                fillOpacity: 1,
                stroke: '#000',
                strokeWidth: 0.688,
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
                strokeDasharray: 'none',
                strokeOpacity: 1,
              }}
            />
            <g
              id="g15"
              style={{
                display: 'inline',
              }}
              transform="matrix(.26458 0 0 .26433 -10.753 -73.87)"
            >
              <path
                id="path5"
                d="M306.76 417.42c0-.19.07-.42.27-.66l5.17-6.09c.35-.41.51-.8.51-1.22 0-.96-.76-1.72-1.72-1.72s-1.66.74-1.7 1.68c-.04.55-.48 1.01-1.04 1.01s-1.04-.48-1.04-1.04c0-2.05 1.68-3.58 3.79-3.58s3.79 1.52 3.79 3.58c0 .69-.28 1.43-.89 2.12l-4.27 4.99h4.21c.51 0 .92.41.92.92s-.41.96-.92.96h-6.13c-.53 0-.96-.42-.96-.96z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.877275,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
              <path
                id="path6"
                d="m304.58 416.66-3.29-3.93.23-.02c1.24-.3 2.3-1.47 2.3-3.17 0-2.05-1.58-3.72-3.9-3.72h-3.01c-.57 0-1.04.46-1.04 1.03v10.48c0 .57.48 1.04 1.04 1.04s1.04-.48 1.04-1.04v-4.09h1.04l3.99 4.74c.21.27.5.39.81.39.57 0 1.03-.46 1.03-1.03 0-.25-.05-.46-.25-.69zm-4.48-5.36h-2.14v-3.54h1.89c1.06 0 1.81.8 1.81 1.79 0 .9-.63 1.64-1.56 1.75z"
                className="cls-4"
                style={{
                  fill: '#f0f',
                  fillOpacity: 1,
                  stroke: '#000',
                  strokeWidth: 0.877275,
                  strokeLinejoin: 'round',
                  strokeMiterlimit: 10,
                  strokeDasharray: 'none',
                }}
              />
            </g>
          </g>
        </g>
      </svg>

      <ButtonMappingDialog
        open={dialogOpen}
        device="controller"
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveButtonBindings}
        initialButton={dialogButton}
      />
    </div>
  );
};

export default Controller;

// helpers moved to controller-code-utils.ts
